import { CREDENTIALS_WORD_LABEL } from "../utils/credentialUiLabels";

export const TERMS_VERSION = "3.0";
export const TERMS_PAGE_COUNT = 47;

const TERMS_CREDENTIALS_SENTENCE =
  `Users are solely responsible for safeguarding account credentials and maintaining the confidentiality of usernames, ${CREDENTIALS_WORD_LABEL.toLowerCase()}, authentication methods, and access devices.`;

export const TERMS_FULL_DOCUMENT_PATH = "/legal/terms";

export const TERMS_SUMMARY_TITLE = "MyTara™ Terms and Conditions";

export type TermsKeyPointIcon =
  | "stethoscope"
  | "bot"
  | "alert"
  | "lock"
  | "user-check"
  | "scroll-text";

export type TermsKeyPoint = {
  id: string;
  icon: TermsKeyPointIcon;
  title: string;
  description: string;
};

/** Short summary shown on the acceptance gate before the user accepts. */
export const TERMS_KEY_POINTS: TermsKeyPoint[] = [
  {
    id: "notMedicalProvider",
    icon: "stethoscope",
    title: "Not a medical provider",
    description:
      "MyTara is a wellness information platform. It does not diagnose, prescribe, or replace a doctor.",
  },
  {
    id: "aiLimitations",
    icon: "bot",
    title: "AI has limitations",
    description:
      "TARA's responses are informational. Always verify important health information with a qualified professional.",
  },
  {
    id: "notForEmergencies",
    icon: "alert",
    title: "Not for emergencies",
    description:
      "In a medical emergency, call 112 or go to the nearest hospital. Do not rely on this app.",
  },
  {
    id: "dataProtected",
    icon: "lock",
    title: "Your data is protected",
    description:
      "We collect health data with your consent under India's DPDPA 2023. You can request deletion at any time.",
  },
  {
    id: "userControl",
    icon: "user-check",
    title: "You're in control",
    description:
      "Your data is never sold. Anonymized, aggregated data may be used to improve the platform.",
  },
  {
    id: "termsMayUpdate",
    icon: "scroll-text",
    title: "Terms may update",
    description:
      "Continued use after changes means you accept revised terms. We'll notify you of significant updates.",
  },
];

export type TermsSummaryPage = {
  page: number;
  title: string;
  paragraphs: string[];
};

export const TERMS_SUMMARY_PAGES: TermsSummaryPage[] = [
  {
    page: 1,
    title: "Introduction",
    paragraphs: [
      "MYTARA™ TERMS AND CONDITIONS",
      "Effective Date: Effective upon publication",
      "These Terms and Conditions (\"Terms\") constitute a legally binding agreement between AUMNIYA LLC (\"Company,\" \"AUMNIYA,\" \"we,\" \"our,\" or \"us\") and each individual or entity accessing or using MyTara™, including any websites, mobile applications, software, artificial intelligence systems, voice interfaces, avatar technologies, APIs, SDKs, and related services (collectively, the \"Platform\").",
      "By accessing or using the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms.",
    ],
  },
  {
    page: 2,
    title: "Article 1: ACCEPTANCE OF TERMS",
    paragraphs: [
      "1.1 Binding Agreement",
      "These Terms constitute a legally binding agreement between the User and AUMNIYA LLC.",
      "By accessing, downloading, installing, registering for, or otherwise using the Platform, the User expressly acknowledges and agrees to these Terms.",
      "1.2 Consent",
      "Use of the Platform constitutes affirmative consent to:",
      "(a) these Terms;",
      "(b) all policies incorporated herein by reference;",
      "(c) future amendments;",
      "(d) applicable laws and regulations.",
      "1.3 Additional Agreements",
      "Certain features, services, products, APIs, SDKs, subscriptions, or integrations may be subject to additional terms and conditions.",
      "In the event of a conflict, such additional terms shall control with respect to the applicable service(s).",
      "1.4 Authority",
      "If the User accesses the Platform on behalf of an organization or another person, the User represents and warrants that the User possesses all necessary authority to bind such entity or person.",
    ],
  },
  {
    page: 3,
    title: "Article 2: DEFINITIONS",
    paragraphs: [
      "For purposes of these Terms:",
      "2.1 \"AI Services\"",
      "means any machine learning, generative artificial intelligence, natural language processing, predictive analytics, automated reasoning, avatar systems, image recognition systems, or future artificial intelligence technologies made available through the Platform.",
      "2.2 \"Avatar\"",
      "means any digital representation, Pixar-style character, animated model, voice-enabled persona, or other virtual representation generated, rendered, customized, or displayed through the Platform.",
      "2.3 \"Caregiver\"",
      "means a family member, legal guardian, healthcare proxy, or other authorized person designated by a User.",
      "2.4 \"Generated Content\"",
      "means summaries, recommendations, wellness insights, analyses, transcripts, reports, AI outputs, or other materials generated by AI Services.",
      "2.5 \"Health Data\"",
      "means information relating to physical health, medical conditions, medications, laboratory information, biometric measurements, symptoms, lifestyle information, images, recordings, and wellness information.",
      "2.6 \"Platform\"",
      "means MyTara™, TARA AI Assistant, associated websites, mobile applications, APIs, SDKs, software, wearables, smart mirrors, voice technologies, avatar technologies, and future services.",
      "2.7 \"TARA AI Assistant\"",
      "means the Company's proprietary artificial intelligence assistant and related systems.",
      "2.8 \"User\"",
      "means any individual or entity using the Platform.",
      "2.9 \"User Content\"",
      "means all information, text, voice recordings, images, videos, medical records, laboratory reports, and other materials uploaded or submitted by a User.",
      "2.10 \"Third-Party Services\"",
      "means products, platforms, applications, healthcare providers, pharmacies, laboratories, wearable devices, software providers, and other services not owned by AUMNIYA LLC.",
    ],
  },
  {
    page: 4,
    title: "Article 3: DESCRIPTION OF SERVICES",
    paragraphs: [
      "3.1 Platform Services",
      "MyTara™ provides a digital health and wellness platform that may include:",
      "(a) TARA AI Assistant;",
      "(b) voice AI technologies;",
      "(c) avatar experiences;",
      "(d) medication tracking;",
      "(e) symptom assessments;",
      "(f) drug interaction checks;",
      "(g) health record management;",
      "(h) laboratory report uploads;",
      "(i) wearable integrations;",
      "(j) smart mirror technologies;",
      "(k) camera-based health assessments;",
      "(l) family account management;",
      "(m) nutrition guidance;",
      "(n) exercise and yoga programs;",
      "(o) chronic disease support;",
      "(p) mental wellness resources;",
      "(q) premium subscriptions;",
      "(r) APIs and SDKs;",
      "(s) future telemedicine and healthcare marketplace services.",
      "3.2 Continuous Evolution",
      "The Platform is dynamic and continuously evolving.",
      "AUMNIYA reserves the right to modify, enhance, discontinue, suspend, or replace any portion of the Platform at any time without liability.",
      "3.3 No Obligation to Maintain Features",
      "The Company does not guarantee the continued availability of any specific feature, functionality, integration, or capability.",
    ],
  },
  {
    page: 5,
    title: "Article 4: NATURE OF PLATFORM",
    paragraphs: [
      "4.1 Informational and Wellness Platform",
      "MyTara™ is intended solely as an informational, educational, wellness, and digital health support platform.",
      "4.2 Not a Healthcare Provider",
      "AUMNIYA LLC is not:",
      "(a) a hospital;",
      "(b) a physician group;",
      "(c) a clinic;",
      "(d) a pharmacy;",
      "(e) a laboratory;",
      "(f) a medical practice;",
      "(g) a healthcare provider.",
      "4.3 No Physician-Patient Relationship",
      "Use of the Platform does not establish:",
      "(a) a physician-patient relationship;",
      "(b) a therapist-patient relationship;",
      "(c) a pharmacist-patient relationship;",
      "(d) any fiduciary relationship.",
      "4.4 Independent Judgment Required",
      "Users remain solely responsible for exercising independent judgment and obtaining professional healthcare advice.",
    ],
  },
  {
    page: 6,
    title: "Article 5: MEDICAL DISCLAIMER",
    paragraphs: [
      "5.1 No Medical Advice",
      "Information made available through the Platform is not intended to constitute:",
      "(a) medical advice;",
      "(b) diagnosis;",
      "(c) treatment;",
      "(d) prescriptions;",
      "(e) healthcare services.",
      "5.2 Professional Consultation",
      "Users should consult qualified physicians and healthcare professionals before making health-related decisions.",
      "5.3 No Substitute",
      "The Platform is not a substitute for:",
      "(a) emergency services;",
      "(b) physician consultations;",
      "(c) professional diagnosis;",
      "(d) licensed healthcare treatment.",
      "5.4 Reliance",
      "Users acknowledge that reliance upon Platform information is entirely at their own risk.",
    ],
  },
  {
    page: 7,
    title: "Article 6: EMERGENCY DISCLAIMER",
    paragraphs: [
      "6.1 No Emergency Services",
      "The Platform does not provide emergency medical services.",
      "6.2 Emergencies",
      "Users experiencing:",
      "(a) chest pain;",
      "(b) breathing difficulties;",
      "(c) severe bleeding;",
      "(d) stroke symptoms;",
      "(e) suicidal thoughts;",
      "(f) seizures;",
      "(g) medical emergencies,",
      "should immediately contact emergency services or seek urgent medical attention.",
      "6.3 No Monitoring",
      "The Company does not continuously monitor Users.",
      "6.4 Delays",
      "Communications through the Platform may experience delays and should never be relied upon during emergencies.",
    ],
  },
  {
    page: 8,
    title: "Article 7: TARA AI ASSISTANT",
    paragraphs: [
      "7.1 Nature of AI Services",
      "TARA AI Assistant utilizes artificial intelligence and machine learning technologies to generate responses and insights.",
      "7.2 Probabilistic Nature",
      "AI-generated outputs are probabilistic and may contain inaccuracies, omissions, errors, or outdated information.",
      "7.3 No Guarantees",
      "AUMNIYA does not warrant that AI outputs are:",
      "(a) accurate;",
      "(b) complete;",
      "(c) medically appropriate;",
      "(d) error-free.",
      "7.4 Verification Required",
      "Users shall independently verify all outputs before acting upon them.",
      "7.5 No Diagnostic Function",
      "TARA AI Assistant does not diagnose diseases or prescribe treatments.",
      "7.6 Future Models",
      "The Company may update, retrain, replace, discontinue, or enhance AI models without notice.",
      "7.7 AI Learning",
      "To the extent permitted by applicable law and privacy policies, anonymized and aggregated information may be used to improve Platform performance.",
    ],
  },
  {
    page: 9,
    title: "Article 8: AVATAR TERMS",
    paragraphs: [
      "8.1 Digital Representations",
      "Avatar features are intended solely for engagement, wellness support, education, and user interaction.",
      "8.2 Fictional Nature",
      "Avatar responses and behaviors are algorithmic and should not be interpreted as human expertise.",
      "8.3 No Healthcare Relationship",
      "Interactions with avatars do not establish any physician-patient relationship.",
      "8.4 Modifications",
      "The Company may modify avatar appearances, voices, personalities, animations, or functionalities at any time.",
      "8.5 User Expectations",
      "Users acknowledge that avatar outputs may contain inaccuracies and are provided on an \"AS IS\" basis.",
      "8.6 Ownership",
      "All avatar technologies, character designs, animation systems, voices, personalities, models, and associated intellectual property remain the exclusive property of AUMNIYA LLC.",
      "8.7 Restrictions",
      "Users shall not copy, reproduce, reverse engineer, commercialize, or exploit avatars without prior written authorization.",
    ],
  },
  {
    page: 10,
    title: "Article 9: VOICE ASSISTANT TERMS",
    paragraphs: [
      "9.1 Voice Features",
      "The Platform may provide voice-enabled functionality, voice commands, voice interactions, speech recognition technologies, and voice-generated responses.",
      "9.2 Recording Consent",
      "By utilizing voice-enabled features, the User consents to the collection, processing, storage, and analysis of voice recordings and transcripts in accordance with applicable law and the Company's Privacy Policy.",
      "9.3 Accuracy Disclaimer",
      "Speech recognition systems are inherently imperfect and may misinterpret words, accents, pronunciations, or contextual meanings.",
      "AUMNIYA makes no representation or warranty regarding the accuracy or completeness of voice transcriptions or responses.",
      "9.4 No Professional Advice",
      "Voice interactions do not constitute medical advice, diagnosis, treatment recommendations, or healthcare services.",
      "9.5 User Responsibility",
      "Users remain solely responsible for independently verifying all information generated through voice interactions.",
    ],
  },
  {
    page: 11,
    title: "Article 10: CAMERA AND IMAGE ANALYSIS DISCLAIMER",
    paragraphs: [
      "10.1 Image-Based Features",
      "The Platform may permit Users to upload photographs, videos, facial images, medical records, prescriptions, laboratory reports, and other visual information.",
      "10.2 Camera-Based Health Monitoring",
      "Future features may include camera-based wellness assessments, smart mirrors, facial analysis technologies, and image-based measurements.",
      "10.3 No Diagnostic Capability",
      "Image analysis features are intended solely for informational and wellness purposes and are not intended to diagnose, treat, cure, or prevent any disease.",
      "10.4 Inherent Limitations",
      "Image quality, lighting conditions, environmental factors, device capabilities, and algorithmic limitations may affect the accuracy of outputs.",
      "10.5 User Assumption of Risk",
      "Users acknowledge and agree that reliance upon image-based outputs is entirely at their own risk.",
    ],
  },
  {
    page: 12,
    title: "Article 11: MEDICATION DISCLAIMER",
    paragraphs: [
      "11.1 Medication Tracking",
      "The Platform may provide medication reminders, schedules, logs, interaction alerts, and informational resources.",
      "11.2 No Prescription Services",
      "The Platform does not prescribe medications and is not a substitute for professional medical advice.",
      "11.3 Interaction Warnings",
      "Drug interaction alerts are informational only and may not identify all interactions, contraindications, allergies, dosage limitations, or adverse events.",
      "11.4 Independent Verification",
      "Users should consult licensed healthcare professionals or pharmacists before initiating, discontinuing, or modifying any medication regimen.",
      "11.5 No Warranty",
      "The Company does not guarantee the completeness, accuracy, or reliability of medication-related information.",
    ],
  },
  {
    page: 13,
    title: "Article 12: LAB RESULTS DISCLAIMER",
    paragraphs: [
      "12.1 Upload Features",
      "Users may upload laboratory reports, pathology records, imaging reports, diagnostic documents, and related materials.",
      "12.2 Informational Purposes",
      "Any summaries, interpretations, or analyses generated by the Platform are provided solely for informational purposes.",
      "12.3 No Diagnosis",
      "The Platform is not intended to diagnose diseases or replace consultation with qualified healthcare professionals.",
      "12.4 Verification Required",
      "Users are solely responsible for reviewing laboratory results with licensed physicians.",
    ],
  },
  {
    page: 14,
    title: "Article 13: FITNESS AND NUTRITION DISCLAIMER",
    paragraphs: [
      "13.1 Wellness Guidance",
      "The Platform may provide fitness programs, yoga routines, meal suggestions, nutrition recommendations, lifestyle guidance, and wellness content.",
      "13.2 Individual Differences",
      "Results may vary among individuals due to age, medical history, genetics, physical condition, and other factors.",
      "13.3 Exercise Risks",
      "Participation in exercise activities carries inherent risks including injury, illness, disability, or death.",
      "13.4 Professional Consultation",
      "Users should consult healthcare professionals before beginning any fitness or dietary program.",
      "13.5 No Guarantees",
      "AUMNIYA does not guarantee weight loss, improved health outcomes, or any specific results.",
    ],
  },
  {
    page: 15,
    title: "Article 14: MENTAL HEALTH DISCLAIMER",
    paragraphs: [
      "14.1 Informational Nature",
      "Mental wellness features are educational and informational in nature.",
      "14.2 No Therapy Services",
      "The Platform does not provide psychotherapy, psychiatric treatment, counseling, crisis intervention, or mental health services.",
      "14.3 Emergency Situations",
      "Users experiencing suicidal ideation, severe emotional distress, or psychiatric emergencies should immediately seek professional assistance.",
      "14.4 No Healthcare Relationship",
      "Use of mental wellness features does not establish a therapist-patient or psychiatrist-patient relationship.",
    ],
  },
  {
    page: 16,
    title: "Article 15: PREGNANCY DISCLAIMER",
    paragraphs: [
      "15.1 Pregnancy Content",
      "The Platform may provide pregnancy-related wellness information and educational materials.",
      "15.2 No Medical Advice",
      "Pregnancy-related information is not intended to replace prenatal care or professional medical guidance.",
      "15.3 High-Risk Pregnancies",
      "Individuals with high-risk pregnancies should seek immediate guidance from qualified healthcare providers.",
      "15.4 User Responsibility",
      "Users acknowledge that pregnancy-related decisions should be made in consultation with licensed medical professionals.",
    ],
  },
  {
    page: 17,
    title: "Article 16: GENETIC AND PHARMACOGENOMIC DISCLAIMER",
    paragraphs: [
      "16.1 Future Features",
      "The Platform may offer pharmacogenomic and genetic wellness capabilities.",
      "16.2 Informational Use",
      "Genetic information and pharmacogenomic insights are intended solely for informational purposes.",
      "16.3 No Clinical Decisions",
      "Genetic outputs should not be used as the sole basis for treatment decisions.",
      "16.4 Professional Interpretation",
      "Users should consult appropriately qualified physicians and specialists regarding genetic information.",
    ],
  },
  {
    page: 18,
    title: "Article 17: FAMILY ACCOUNTS",
    paragraphs: [
      "17.1 Authorized Users",
      "The Platform may permit Users to create family accounts, dependent profiles, and caregiver accounts.",
      "17.2 Authority",
      "Users represent and warrant that they possess all necessary authority to manage information relating to dependents or other authorized individuals.",
      "17.3 Caregiver Access",
      "Caregivers may access information only to the extent authorized by applicable law and the relevant User.",
      "17.4 User Responsibility",
      "The primary account holder remains responsible for all activity conducted through associated accounts.",
      "17.5 Minors",
      "Parents or legal guardians are solely responsible for supervising minors utilizing the Platform.",
    ],
  },
  {
    page: 19,
    title: "Article 18: ELIGIBILITY",
    paragraphs: [
      "18.1 Legal Capacity",
      "Users must possess the legal capacity to enter into binding agreements.",
      "18.2 Minimum Age",
      "Users under eighteen (18) years of age may use the Platform only with parental or legal guardian authorization.",
      "18.3 Prohibited Jurisdictions",
      "The Platform may not be available in certain jurisdictions.",
      "18.4 Compliance with Laws",
      "Users agree to comply with all applicable laws, rules, and regulations.",
      "18.5 Suspension of Access",
      "The Company reserves the right to restrict access to any User who fails to satisfy eligibility requirements.",
    ],
  },
  {
    page: 20,
    title: "Article 19: USER ACCOUNTS",
    paragraphs: [
      "19.1 Registration",
      "Certain features of the Platform require the creation of an account.",
      "Users agree to provide accurate, complete, and current information and to promptly update such information as necessary.",
      "19.2 Credentials",
      TERMS_CREDENTIALS_SENTENCE,
      "19.3 Unauthorized Access",
      "Users shall immediately notify AUMNIYA LLC of any unauthorized access, misuse, or suspected compromise of their accounts.",
      "19.4 Responsibility for Activities",
      "Users are responsible for all activities occurring under their accounts, whether authorized or unauthorized.",
      "19.5 Verification",
      "The Company reserves the right to require identity verification and additional authentication procedures.",
    ],
  },
  {
    page: 21,
    title: "Article 20: USER CONTENT",
    paragraphs: [
      "20.1 Ownership",
      "Subject to the licenses granted herein, Users retain ownership rights in User Content.",
      "20.2 Responsibility",
      "Users are solely responsible for all User Content uploaded, transmitted, stored, or otherwise made available through the Platform.",
      "20.3 Accuracy",
      "Users represent and warrant that User Content is accurate and that they possess all rights necessary to provide such content.",
      "20.4 Prohibited Content",
      "Users shall not upload content that:",
      "(a) violates applicable laws;",
      "(b) infringes intellectual property rights;",
      "(c) contains malicious code;",
      "(d) is fraudulent or deceptive;",
      "(e) is defamatory, obscene, or unlawful;",
      "(f) violates privacy rights.",
      "20.5 Monitoring",
      "The Company shall have no obligation to monitor User Content but reserves the right to review, remove, or restrict access to such content.",
    ],
  },
  {
    page: 22,
    title: "Article 21: LICENSE GRANTED BY USERS",
    paragraphs: [
      "21.1 Platform License",
      "Users grant AUMNIYA LLC a worldwide, non-exclusive, royalty-free, transferable, sublicensable license to host, reproduce, process, transmit, analyze, store, and display User Content solely for the purpose of operating and improving the Platform.",
      "21.2 Technical Copies",
      "Users authorize the creation of temporary, backup, archival, and technical copies required for operation of the Platform.",
      "21.3 Anonymization",
      "To the extent permitted by law, de-identified and aggregated information may be utilized for analytics, machine learning, product enhancement, and research.",
      "21.4 Survival",
      "Licenses reasonably necessary for legal compliance, backup, and archival purposes shall survive termination.",
    ],
  },
  {
    page: 23,
    title: "Article 22: PROHIBITED CONDUCT",
    paragraphs: [
      "Users shall not:",
      "(a) reverse engineer the Platform;",
      "(b) interfere with Platform operations;",
      "(c) circumvent security mechanisms;",
      "(d) use automated bots without authorization;",
      "(e) scrape or extract data;",
      "(f) exploit vulnerabilities;",
      "(g) upload malicious software;",
      "(h) impersonate another person;",
      "(i) engage in unlawful conduct;",
      "(j) violate applicable healthcare laws;",
      "(k) infringe intellectual property rights;",
      "(l) use the Platform to create competing services;",
      "(m) attempt unauthorized access to systems;",
      "(n) misuse APIs or SDKs.",
      "Violation of this Article may result in immediate suspension or termination.",
    ],
  },
  {
    page: 24,
    title: "Article 23: THIRD-PARTY SERVICES",
    paragraphs: [
      "23.1 Third-Party Providers",
      "The Platform may integrate with third-party products and services.",
      "23.2 No Responsibility",
      "AUMNIYA LLC does not own or control third-party services and assumes no responsibility for their availability, content, security, functionality, or accuracy.",
      "23.3 Independent Terms",
      "Use of third-party services may be governed by separate agreements.",
      "23.4 Disclaimer",
      "The Company disclaims all liability arising from third-party products and services.",
    ],
  },
  {
    page: 25,
    title: "Article 24: WEARABLE DEVICES",
    paragraphs: [
      "24.1 Integrations",
      "The Platform may support wearable devices and related technologies.",
      "24.2 Device Limitations",
      "Device measurements may be inaccurate, delayed, interrupted, or unavailable.",
      "24.3 No Guarantee",
      "The Company does not guarantee the accuracy or reliability of device-generated information.",
      "24.4 Independent Verification",
      "Users should independently verify measurements before relying upon them.",
    ],
  },
  {
    page: 26,
    title: "Article 25: SMART MIRRORS AND CAMERA-BASED SYSTEMS",
    paragraphs: [
      "25.1 Future Technologies",
      "The Platform may support smart mirrors, camera systems, facial analysis technologies, and related health-monitoring capabilities.",
      "25.2 Informational Purposes",
      "Outputs generated through such technologies are intended solely for wellness and informational purposes.",
      "25.3 No Medical Device Claims",
      "Nothing within the Platform should be interpreted as representing FDA approval, CDSCO approval, or regulatory certification unless expressly stated.",
      "25.4 Accuracy Disclaimer",
      "Environmental conditions and technical limitations may affect performance.",
    ],
  },
  {
    page: 27,
    title: "Article 26: SUBSCRIPTIONS",
    paragraphs: [
      "26.1 Paid Services",
      "Certain features may require paid subscriptions.",
      "26.2 Billing",
      "Subscription fees shall be charged according to the selected plan.",
      "26.3 Automatic Renewal",
      "Unless otherwise specified, subscriptions automatically renew.",
      "26.4 Cancellation",
      "Users may cancel subscriptions in accordance with applicable policies.",
      "26.5 Modifications",
      "The Company may modify pricing, plans, features, or subscription structures upon reasonable notice.",
    ],
  },
  {
    page: 28,
    title: "Article 27: PAYMENTS",
    paragraphs: [
      "27.1 Payment Methods",
      "Users authorize the Company and its payment processors to charge payment methods provided by Users.",
      "27.2 Taxes",
      "Fees are exclusive of taxes unless otherwise required by law.",
      "27.3 Failed Payments",
      "The Company reserves the right to suspend services for failed or declined payments.",
      "27.4 Collection",
      "The Company may utilize collection agencies and pursue lawful remedies for unpaid amounts.",
    ],
  },
  {
    page: 29,
    title: "Article 28: REFUNDS",
    paragraphs: [
      "28.1 General Rule",
      "Except as required by applicable law, payments are non-refundable.",
      "28.2 Promotional Services",
      "Promotional discounts and credits possess no cash value.",
      "28.3 Billing Errors",
      "Verified billing errors may be corrected at the Company's discretion.",
      "28.4 Chargebacks",
      "Improper chargebacks may result in account suspension and recovery actions.",
    ],
  },
  {
    page: 30,
    title: "Article 29: INTELLECTUAL PROPERTY",
    paragraphs: [
      "29.1 Ownership",
      "The Platform and all related technologies, software, source code, object code, algorithms, models, databases, interfaces, graphics, layouts, text, content, trademarks, logos, audiovisual materials, workflows, designs, documentation, APIs, SDKs, and all associated intellectual property rights are and shall remain the exclusive property of AUMNIYA LLC and its licensors.",
      "29.2 Reservation of Rights",
      "Except for the limited rights expressly granted herein, no rights, title, or interests are transferred to Users.",
      "29.3 Proprietary Rights",
      "All patents, copyrights, trade secrets, trademarks, know-how, and other intellectual property rights are reserved.",
      "29.4 Restrictions",
      "Users shall not:",
      "(a) copy;",
      "(b) reproduce;",
      "(c) distribute;",
      "(d) reverse engineer;",
      "(e) modify;",
      "(f) create derivative works;",
      "(g) commercially exploit;",
      "(h) sublicense;",
      "(i) sell or lease any portion of the Platform.",
    ],
  },
  {
    page: 31,
    title: "Article 30: OWNERSHIP OF GENERATED CONTENT",
    paragraphs: [
      "30.1 AI Outputs",
      "Subject to applicable law and these Terms, Users may utilize Generated Content produced specifically for them.",
      "30.2 Company Ownership",
      "The Company retains ownership of:",
      "(a) AI models;",
      "(b) training methodologies;",
      "(c) prompts;",
      "(d) algorithms;",
      "(e) software;",
      "(f) datasets;",
      "(g) workflows;",
      "(h) system architectures.",
      "30.3 Similar Outputs",
      "Due to the nature of artificial intelligence, outputs generated for different users may be identical or substantially similar.",
      "30.4 No Exclusivity",
      "The Company does not guarantee exclusivity of Generated Content.",
      "30.5 Feedback",
      "Any suggestions, feedback, or ideas submitted by Users may be used by AUMNIYA LLC without restriction or compensation.",
    ],
  },
  {
    page: 32,
    title: "Article 31: COPYRIGHT POLICY",
    paragraphs: [
      "31.1 Respect for Rights",
      "The Company respects intellectual property rights.",
      "31.2 Infringement Notices",
      "Claims of copyright infringement shall contain sufficient information to identify:",
      "(a) the copyrighted work;",
      "(b) the allegedly infringing material;",
      "(c) ownership rights;",
      "(d) contact information;",
      "(e) a statement made in good faith.",
      "31.3 Removal",
      "The Company reserves the right to remove allegedly infringing content.",
      "31.4 Repeat Infringers",
      "Accounts associated with repeated violations may be terminated.",
    ],
  },
  {
    page: 33,
    title: "Article 32: TRADEMARK PROTECTION",
    paragraphs: [
      "32.1 Marks",
      "\"MyTara™,\" \"TARA AI Assistant,\" and associated marks are proprietary trademarks of AUMNIYA LLC.",
      "32.2 Restrictions",
      "Users shall not use Company trademarks without prior written authorization.",
      "32.3 No Implied Rights",
      "Nothing contained herein grants any trademark license.",
    ],
  },
  {
    page: 34,
    title: "Article 33: APIS AND SDKS",
    paragraphs: [
      "33.1 Availability",
      "The Company may make APIs, SDKs, developer tools, and technical documentation available.",
      "33.2 Restrictions",
      "Users shall not:",
      "(a) misuse APIs;",
      "(b) exceed rate limits;",
      "(c) compromise security;",
      "(d) access unauthorized data;",
      "(e) create unlawful applications.",
      "33.3 Modifications",
      "APIs and SDKs may be modified or discontinued at any time.",
      "33.4 Suspension",
      "The Company may suspend developer access without liability.",
    ],
  },
  {
    page: 35,
    title: "Article 34: SERVICE AVAILABILITY",
    paragraphs: [
      "34.1 No Guarantee",
      "The Platform is provided on an availability basis and uninterrupted access is not guaranteed.",
      "34.2 Maintenance",
      "The Company may perform maintenance, upgrades, and modifications.",
      "34.3 Interruptions",
      "Interruptions may occur because of:",
      "(a) system failures;",
      "(b) cyberattacks;",
      "(c) third-party outages;",
      "(d) force majeure events;",
      "(e) network disruptions.",
      "34.4 No Liability",
      "The Company shall not be liable for service interruptions.",
    ],
  },
  {
    page: 36,
    title: "Article 35: SUSPENSION",
    paragraphs: [
      "35.1 Grounds",
      "The Company may suspend access for:",
      "(a) security concerns;",
      "(b) violations of these Terms;",
      "(c) fraudulent activity;",
      "(d) nonpayment;",
      "(e) legal requirements.",
      "35.2 Immediate Action",
      "Suspension may occur without prior notice.",
      "35.3 Investigation",
      "The Company may investigate suspected misconduct.",
    ],
  },
  {
    page: 37,
    title: "Article 36: TERMINATION",
    paragraphs: [
      "36.1 User Termination",
      "Users may terminate accounts at any time.",
      "36.2 Company Termination",
      "The Company may terminate accounts for any lawful reason.",
      "36.3 Effect of Termination",
      "Upon termination:",
      "(a) access rights cease;",
      "(b) licenses granted by the Company terminate;",
      "(c) certain information may be retained as required by law.",
      "36.4 Survival",
      "Provisions relating to intellectual property, limitations of liability, indemnification, dispute resolution, and other provisions intended to survive shall survive termination.",
    ],
  },
  {
    page: 38,
    title: "Article 37: DISCLAIMER OF WARRANTIES",
    paragraphs: [
      "37.1 \"AS IS\"",
      "THE PLATFORM IS PROVIDED \"AS IS,\" \"AS AVAILABLE,\" AND WITH ALL FAULTS.",
      "37.2 No Warranties",
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW, AUMNIYA LLC DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING:",
      "(a) MERCHANTABILITY;",
      "(b) FITNESS FOR A PARTICULAR PURPOSE;",
      "(c) NON-INFRINGEMENT;",
      "(d) ACCURACY;",
      "(e) RELIABILITY;",
      "(f) SECURITY;",
      "(g) AVAILABILITY.",
      "37.3 AI Limitations",
      "THE COMPANY DOES NOT WARRANT THAT AI OUTPUTS WILL BE ACCURATE, COMPLETE, OR ERROR-FREE.",
      "37.4 THIRD-PARTY SERVICES",
      "THE COMPANY DISCLAIMS ALL RESPONSIBILITY FOR THIRD-PARTY PRODUCTS AND SERVICES.",
    ],
  },
  {
    page: 39,
    title: "Article 38: LIMITATION OF LIABILITY",
    paragraphs: [
      "38.1 Excluded Damages",
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW, AUMNIYA LLC SHALL NOT BE LIABLE FOR:",
      "(a) INDIRECT DAMAGES;",
      "(b) INCIDENTAL DAMAGES;",
      "(c) SPECIAL DAMAGES;",
      "(d) CONSEQUENTIAL DAMAGES;",
      "(e) EXEMPLARY DAMAGES;",
      "(f) LOSS OF PROFITS;",
      "(g) LOSS OF DATA;",
      "(h) LOSS OF GOODWILL;",
      "(i) BUSINESS INTERRUPTION.",
      "38.2 Aggregate Liability Cap",
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE AGGREGATE LIABILITY OF AUMNIYA LLC SHALL NOT EXCEED THE GREATER OF:",
      "(a) ONE HUNDRED UNITED STATES DOLLARS (US$100); OR",
      "(b) THE AMOUNTS PAID BY THE USER TO THE COMPANY DURING THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.",
      "38.3 Basis of Bargain",
      "THE PARTIES ACKNOWLEDGE THAT THE LIMITATIONS CONTAINED HEREIN FORM AN ESSENTIAL BASIS OF THE BARGAIN BETWEEN THE PARTIES.",
    ],
  },
  {
    page: 40,
    title: "Article 39: INDEMNIFICATION",
    paragraphs: [
      "39.1 User Indemnification",
      "To the maximum extent permitted by applicable law, Users agree to defend, indemnify, and hold harmless AUMNIYA LLC and its affiliates, subsidiaries, parent companies, members, managers, officers, directors, employees, contractors, licensors, agents, successors, and assigns from and against any and all claims, actions, liabilities, losses, damages, judgments, penalties, fines, settlements, costs, and expenses, including reasonable attorneys' fees, arising out of or relating to:",
      "(a) use of the Platform;",
      "(b) User Content;",
      "(c) violation of these Terms;",
      "(d) violation of applicable laws;",
      "(e) infringement of intellectual property rights;",
      "(f) misuse of AI-generated outputs;",
      "(g) reliance upon information obtained through the Platform;",
      "(h) interactions with third parties.",
      "39.2 Cooperation",
      "Users shall cooperate fully in the defense of any matter subject to indemnification.",
      "39.3 Control of Defense",
      "AUMNIYA LLC reserves the exclusive right to assume the defense and settlement of any claim.",
    ],
  },
  {
    page: 41,
    title: "Article 40: FORCE MAJEURE",
    paragraphs: [
      "40.1 Excused Performance",
      "Neither party shall be liable for delays or failures in performance resulting from circumstances beyond reasonable control.",
      "40.2 Events",
      "Force majeure events include:",
      "(a) natural disasters;",
      "(b) pandemics;",
      "(c) epidemics;",
      "(d) war;",
      "(e) terrorism;",
      "(f) civil unrest;",
      "(g) governmental actions;",
      "(h) labor disputes;",
      "(i) internet outages;",
      "(j) cyberattacks;",
      "(k) power failures;",
      "(l) telecommunications failures;",
      "(m) failures of suppliers and service providers.",
      "40.3 Resumption",
      "Affected obligations shall resume when the force majeure event ceases.",
    ],
  },
  {
    page: 42,
    title: "Article 41: DATA PROTECTION - INDIA (DPDPA 2023)",
    paragraphs: [
      "41.1 Applicable Law",
      "The collection, processing, storage, and transfer of personal data and Health Data relating to Users located in India is governed by the Digital Personal Data Protection Act, 2023 (“DPDPA”) and rules made thereunder.",
      "41.2 Data Fiduciary",
      "AUMNIYA LLC acts as the Data Fiduciary with respect to personal data collected through the Platform.",
      "41.3 Consent",
      "The Company collects and processes personal data and Health Data only upon obtaining free, specific, informed, unconditional, and unambiguous consent from the User, as required under the DPDPA.",
      "Consent is obtained prior to or at the point of data collection.",
      "Users may withdraw consent at any time by contacting the Company at legal@mytara.ai.",
      "Withdrawal of consent shall not affect the lawfulness of processing prior to withdrawal but may result in the inability to access certain features of the Platform.",
      "41.4 Purpose Limitation",
      "Personal data and Health Data collected through the Platform is used solely for the purposes disclosed at the time of collection, including but not limited to:",
      "(a) providing Platform features and services;",
      "(b) improving AI model performance using anonymized and aggregated data;",
      "(c) communicating service updates and notifications; and",
      "(d) complying with legal obligations.",
      "41.5 Data Principal Rights",
      "Users, as Data Principals under the DPDPA, have the right to:",
      "(a) obtain a summary of personal data held by the Company;",
      "(b) correct or update inaccurate personal data;",
      "(c) request erasure of personal data where the purpose of collection has been fulfilled or consent has been withdrawn;",
      "(d) nominate another individual to exercise these rights in the event of death or incapacity; and",
      "(e) grieve any action or inaction of the Company with respect to personal data.",
      "41.6 Grievance Redressal",
      "Users may submit data-related grievances to the Company’s designated Grievance Officer:",
      "Email: legal@mytara.ai",
      "Website: www.mytara.ai",
      "The Company shall acknowledge grievances within 48 hours and resolve them within a period not exceeding 30 days from receipt.",
      "41.7 Data Retention",
      "Personal data and Health Data shall not be retained beyond the period necessary to fulfil the purpose for which it was collected, or as required by applicable law, whichever is later.",
      "Upon expiry of the retention period, data shall be deleted or anonymized.",
      "41.8 Cross-Border Transfers",
      "Where personal data is transferred outside India, such transfers shall be conducted only in accordance with applicable provisions of the DPDPA and any rules or notifications issued by the Government of India regarding permissible transfer destinations.",
      "41.9 Children’s Data",
      "The Company does not knowingly collect personal data from children under the age of eighteen (18) without verifiable parental or guardian consent, as required under the DPDPA.",
    ],
  },
  {
    page: 43,
    title: "Article 42: GOVERNING LAW",
    paragraphs: [
      "42.1 Applicable Law",
      "These Terms shall be governed by and construed in accordance with the laws of India, without regard to principles of conflicts of laws.",
      "42.2 Mandatory Laws",
      "Nothing contained herein shall exclude mandatory consumer protections or statutory rights that cannot lawfully be waived.",
    ],
  },
  {
    page: 44,
    title: "Article 43: ARBITRATION",
    paragraphs: [
      "43.1 Arbitration Agreement",
      "Except where prohibited by law, any dispute, controversy, or claim arising out of or relating to these Terms or the Platform shall be resolved exclusively through binding arbitration.",
      "43.2 Applicable Law",
      "Arbitration shall be conducted pursuant to the Arbitration and Conciliation Act, 1996.",
      "43.3 Seat and Venue",
      "The seat and venue of arbitration shall be New Delhi, India.",
      "43.4 Language",
      "Arbitration proceedings shall be conducted in the English language.",
      "43.5 Number of Arbitrators",
      "Disputes shall be resolved by a sole arbitrator appointed in accordance with applicable law.",
      "43.6 Confidentiality",
      "Arbitration proceedings and awards shall remain confidential.",
      "43.7 Equitable Relief",
      "Nothing herein shall prevent either party from seeking temporary or permanent equitable relief from courts having jurisdiction.",
    ],
  },
  {
    page: 45,
    title: "Article 44: CLASS ACTION WAIVER",
    paragraphs: [
      "44.1 Individual Claims",
      "To the fullest extent permitted by applicable law, Users agree that disputes shall be brought solely in an individual capacity.",
      "44.2 No Representative Actions",
      "Users waive any right to participate in:",
      "(a) class actions;",
      "(b) collective actions;",
      "(c) representative actions;",
      "(d) consolidated proceedings.",
      "44.3 Severability",
      "If this waiver is determined to be unenforceable, the remaining provisions shall remain in effect to the maximum extent permitted by law.",
    ],
  },
  {
    page: 46,
    title: "Article 45: INJUNCTIVE RELIEF",
    paragraphs: [
      "45.1 Equitable Remedies",
      "Users acknowledge that unauthorized use of the Platform may result in irreparable harm.",
      "45.2 Right to Injunctive Relief",
      "AUMNIYA LLC shall be entitled to seek injunctive relief, equitable relief, and specific performance without the necessity of posting bond or proving actual damages.",
    ],
  },
  {
    page: 47,
    title: "Article 46: GENERAL PROVISIONS",
    paragraphs: [
      "46.1 Entire Agreement",
      "These Terms constitute the complete and exclusive agreement between the parties and supersede all prior agreements, understandings, and communications.",
      "46.2 Modifications",
      "AUMNIYA LLC reserves the right to amend these Terms at any time.",
      "Continued use of the Platform constitutes acceptance of revised Terms.",
      "46.3 Assignment",
      "Users may not assign rights or obligations without prior written consent.",
      "AUMNIYA LLC may assign these Terms without restriction.",
      "46.4 Waiver",
      "Failure to enforce any provision shall not constitute a waiver.",
      "46.5 Severability",
      "If any provision is found invalid or unenforceable, the remaining provisions shall remain effective.",
      "46.6 Relationship of Parties",
      "Nothing herein creates a partnership, joint venture, employment relationship, agency relationship, or fiduciary relationship.",
      "46.7 No Third-Party Beneficiaries",
      "Except as expressly stated, these Terms confer no rights upon third parties.",
      "46.8 Electronic Communications",
      "Users consent to receiving notices and communications electronically.",
      "Electronic records and signatures shall have the same force and effect as physical documents and handwritten signatures.",
      "46.9 Export Compliance",
      "Users agree to comply with all applicable export control laws and sanctions regulations.",
      "46.10 Headings",
      "Article and section headings are included solely for convenience and shall not affect interpretation.",
      "46.11 Survival",
      "Any provisions which by their nature should survive termination shall survive termination.",
      "46.12 Contact Information",
      "Questions regarding these Terms may be directed to:",
      "AUMNIYA LLC",
      "MyTara™ Platform",
      "Legal Department",
      "Email: legal@mytara.ai",
      "Website: www.mytara.ai",
      "ACKNOWLEDGMENT",
      "BY ACCESSING OR USING MYTARA™, THE USER ACKNOWLEDGES THAT THE USER HAS READ, UNDERSTOOD, AND AGREES TO BE LEGALLY BOUND BY THESE TERMS AND CONDITIONS.",
      "THE USER FURTHER ACKNOWLEDGES THAT THE PLATFORM IS NOT A SUBSTITUTE FOR PROFESSIONAL MEDICAL CARE, DIAGNOSIS, OR TREATMENT.",
    ],
  }
];

/** Flat sections for register consent preview */
export const TERMS_SUMMARY_SECTIONS = TERMS_SUMMARY_PAGES.map((page) => ({
  title: `Page ${page.page}: ${page.title}`,
  body: page.paragraphs[0] ?? "",
}));

export const TERMS_FULL_SECTIONS: Array<{ title: string; paragraphs: string[] }> = [
  {
    title: "Introduction",
    paragraphs: [
      "MYTARA™ TERMS AND CONDITIONS",
      "Effective Date: Effective upon publication",
      "These Terms and Conditions (\"Terms\") constitute a legally binding agreement between AUMNIYA LLC (\"Company,\" \"AUMNIYA,\" \"we,\" \"our,\" or \"us\") and each individual or entity accessing or using MyTara™, including any websites, mobile applications, software, artificial intelligence systems, voice interfaces, avatar technologies, APIs, SDKs, and related services (collectively, the \"Platform\").",
      "By accessing or using the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms.",
    ],
  },
  {
    title: "1. ACCEPTANCE OF TERMS",
    paragraphs: [
      "1.1 Binding Agreement",
      "These Terms constitute a legally binding agreement between the User and AUMNIYA LLC.",
      "By accessing, downloading, installing, registering for, or otherwise using the Platform, the User expressly acknowledges and agrees to these Terms.",
      "1.2 Consent",
      "Use of the Platform constitutes affirmative consent to:",
      "(a) these Terms;",
      "(b) all policies incorporated herein by reference;",
      "(c) future amendments;",
      "(d) applicable laws and regulations.",
      "1.3 Additional Agreements",
      "Certain features, services, products, APIs, SDKs, subscriptions, or integrations may be subject to additional terms and conditions.",
      "In the event of a conflict, such additional terms shall control with respect to the applicable service(s).",
      "1.4 Authority",
      "If the User accesses the Platform on behalf of an organization or another person, the User represents and warrants that the User possesses all necessary authority to bind such entity or person.",
    ],
  },
  {
    title: "2. DEFINITIONS",
    paragraphs: [
      "For purposes of these Terms:",
      "2.1 \"AI Services\"",
      "means any machine learning, generative artificial intelligence, natural language processing, predictive analytics, automated reasoning, avatar systems, image recognition systems, or future artificial intelligence technologies made available through the Platform.",
      "2.2 \"Avatar\"",
      "means any digital representation, Pixar-style character, animated model, voice-enabled persona, or other virtual representation generated, rendered, customized, or displayed through the Platform.",
      "2.3 \"Caregiver\"",
      "means a family member, legal guardian, healthcare proxy, or other authorized person designated by a User.",
      "2.4 \"Generated Content\"",
      "means summaries, recommendations, wellness insights, analyses, transcripts, reports, AI outputs, or other materials generated by AI Services.",
      "2.5 \"Health Data\"",
      "means information relating to physical health, medical conditions, medications, laboratory information, biometric measurements, symptoms, lifestyle information, images, recordings, and wellness information.",
      "2.6 \"Platform\"",
      "means MyTara™, TARA AI Assistant, associated websites, mobile applications, APIs, SDKs, software, wearables, smart mirrors, voice technologies, avatar technologies, and future services.",
      "2.7 \"TARA AI Assistant\"",
      "means the Company's proprietary artificial intelligence assistant and related systems.",
      "2.8 \"User\"",
      "means any individual or entity using the Platform.",
      "2.9 \"User Content\"",
      "means all information, text, voice recordings, images, videos, medical records, laboratory reports, and other materials uploaded or submitted by a User.",
      "2.10 \"Third-Party Services\"",
      "means products, platforms, applications, healthcare providers, pharmacies, laboratories, wearable devices, software providers, and other services not owned by AUMNIYA LLC.",
    ],
  },
  {
    title: "3. DESCRIPTION OF SERVICES",
    paragraphs: [
      "3.1 Platform Services",
      "MyTara™ provides a digital health and wellness platform that may include:",
      "(a) TARA AI Assistant;",
      "(b) voice AI technologies;",
      "(c) avatar experiences;",
      "(d) medication tracking;",
      "(e) symptom assessments;",
      "(f) drug interaction checks;",
      "(g) health record management;",
      "(h) laboratory report uploads;",
      "(i) wearable integrations;",
      "(j) smart mirror technologies;",
      "(k) camera-based health assessments;",
      "(l) family account management;",
      "(m) nutrition guidance;",
      "(n) exercise and yoga programs;",
      "(o) chronic disease support;",
      "(p) mental wellness resources;",
      "(q) premium subscriptions;",
      "(r) APIs and SDKs;",
      "(s) future telemedicine and healthcare marketplace services.",
      "3.2 Continuous Evolution",
      "The Platform is dynamic and continuously evolving.",
      "AUMNIYA reserves the right to modify, enhance, discontinue, suspend, or replace any portion of the Platform at any time without liability.",
      "3.3 No Obligation to Maintain Features",
      "The Company does not guarantee the continued availability of any specific feature, functionality, integration, or capability.",
    ],
  },
  {
    title: "4. NATURE OF PLATFORM",
    paragraphs: [
      "4.1 Informational and Wellness Platform",
      "MyTara™ is intended solely as an informational, educational, wellness, and digital health support platform.",
      "4.2 Not a Healthcare Provider",
      "AUMNIYA LLC is not:",
      "(a) a hospital;",
      "(b) a physician group;",
      "(c) a clinic;",
      "(d) a pharmacy;",
      "(e) a laboratory;",
      "(f) a medical practice;",
      "(g) a healthcare provider.",
      "4.3 No Physician-Patient Relationship",
      "Use of the Platform does not establish:",
      "(a) a physician-patient relationship;",
      "(b) a therapist-patient relationship;",
      "(c) a pharmacist-patient relationship;",
      "(d) any fiduciary relationship.",
      "4.4 Independent Judgment Required",
      "Users remain solely responsible for exercising independent judgment and obtaining professional healthcare advice.",
    ],
  },
  {
    title: "5. MEDICAL DISCLAIMER",
    paragraphs: [
      "5.1 No Medical Advice",
      "Information made available through the Platform is not intended to constitute:",
      "(a) medical advice;",
      "(b) diagnosis;",
      "(c) treatment;",
      "(d) prescriptions;",
      "(e) healthcare services.",
      "5.2 Professional Consultation",
      "Users should consult qualified physicians and healthcare professionals before making health-related decisions.",
      "5.3 No Substitute",
      "The Platform is not a substitute for:",
      "(a) emergency services;",
      "(b) physician consultations;",
      "(c) professional diagnosis;",
      "(d) licensed healthcare treatment.",
      "5.4 Reliance",
      "Users acknowledge that reliance upon Platform information is entirely at their own risk.",
    ],
  },
  {
    title: "6. EMERGENCY DISCLAIMER",
    paragraphs: [
      "6.1 No Emergency Services",
      "The Platform does not provide emergency medical services.",
      "6.2 Emergencies",
      "Users experiencing:",
      "(a) chest pain;",
      "(b) breathing difficulties;",
      "(c) severe bleeding;",
      "(d) stroke symptoms;",
      "(e) suicidal thoughts;",
      "(f) seizures;",
      "(g) medical emergencies,",
      "should immediately contact emergency services or seek urgent medical attention.",
      "6.3 No Monitoring",
      "The Company does not continuously monitor Users.",
      "6.4 Delays",
      "Communications through the Platform may experience delays and should never be relied upon during emergencies.",
    ],
  },
  {
    title: "7. TARA AI ASSISTANT",
    paragraphs: [
      "7.1 Nature of AI Services",
      "TARA AI Assistant utilizes artificial intelligence and machine learning technologies to generate responses and insights.",
      "7.2 Probabilistic Nature",
      "AI-generated outputs are probabilistic and may contain inaccuracies, omissions, errors, or outdated information.",
      "7.3 No Guarantees",
      "AUMNIYA does not warrant that AI outputs are:",
      "(a) accurate;",
      "(b) complete;",
      "(c) medically appropriate;",
      "(d) error-free.",
      "7.4 Verification Required",
      "Users shall independently verify all outputs before acting upon them.",
      "7.5 No Diagnostic Function",
      "TARA AI Assistant does not diagnose diseases or prescribe treatments.",
      "7.6 Future Models",
      "The Company may update, retrain, replace, discontinue, or enhance AI models without notice.",
      "7.7 AI Learning",
      "To the extent permitted by applicable law and privacy policies, anonymized and aggregated information may be used to improve Platform performance.",
    ],
  },
  {
    title: "8. AVATAR TERMS",
    paragraphs: [
      "8.1 Digital Representations",
      "Avatar features are intended solely for engagement, wellness support, education, and user interaction.",
      "8.2 Fictional Nature",
      "Avatar responses and behaviors are algorithmic and should not be interpreted as human expertise.",
      "8.3 No Healthcare Relationship",
      "Interactions with avatars do not establish any physician-patient relationship.",
      "8.4 Modifications",
      "The Company may modify avatar appearances, voices, personalities, animations, or functionalities at any time.",
      "8.5 User Expectations",
      "Users acknowledge that avatar outputs may contain inaccuracies and are provided on an \"AS IS\" basis.",
      "8.6 Ownership",
      "All avatar technologies, character designs, animation systems, voices, personalities, models, and associated intellectual property remain the exclusive property of AUMNIYA LLC.",
      "8.7 Restrictions",
      "Users shall not copy, reproduce, reverse engineer, commercialize, or exploit avatars without prior written authorization.",
    ],
  },
  {
    title: "9. VOICE ASSISTANT TERMS",
    paragraphs: [
      "9.1 Voice Features",
      "The Platform may provide voice-enabled functionality, voice commands, voice interactions, speech recognition technologies, and voice-generated responses.",
      "9.2 Recording Consent",
      "By utilizing voice-enabled features, the User consents to the collection, processing, storage, and analysis of voice recordings and transcripts in accordance with applicable law and the Company's Privacy Policy.",
      "9.3 Accuracy Disclaimer",
      "Speech recognition systems are inherently imperfect and may misinterpret words, accents, pronunciations, or contextual meanings.",
      "AUMNIYA makes no representation or warranty regarding the accuracy or completeness of voice transcriptions or responses.",
      "9.4 No Professional Advice",
      "Voice interactions do not constitute medical advice, diagnosis, treatment recommendations, or healthcare services.",
      "9.5 User Responsibility",
      "Users remain solely responsible for independently verifying all information generated through voice interactions.",
    ],
  },
  {
    title: "10. CAMERA AND IMAGE ANALYSIS DISCLAIMER",
    paragraphs: [
      "10.1 Image-Based Features",
      "The Platform may permit Users to upload photographs, videos, facial images, medical records, prescriptions, laboratory reports, and other visual information.",
      "10.2 Camera-Based Health Monitoring",
      "Future features may include camera-based wellness assessments, smart mirrors, facial analysis technologies, and image-based measurements.",
      "10.3 No Diagnostic Capability",
      "Image analysis features are intended solely for informational and wellness purposes and are not intended to diagnose, treat, cure, or prevent any disease.",
      "10.4 Inherent Limitations",
      "Image quality, lighting conditions, environmental factors, device capabilities, and algorithmic limitations may affect the accuracy of outputs.",
      "10.5 User Assumption of Risk",
      "Users acknowledge and agree that reliance upon image-based outputs is entirely at their own risk.",
    ],
  },
  {
    title: "11. MEDICATION DISCLAIMER",
    paragraphs: [
      "11.1 Medication Tracking",
      "The Platform may provide medication reminders, schedules, logs, interaction alerts, and informational resources.",
      "11.2 No Prescription Services",
      "The Platform does not prescribe medications and is not a substitute for professional medical advice.",
      "11.3 Interaction Warnings",
      "Drug interaction alerts are informational only and may not identify all interactions, contraindications, allergies, dosage limitations, or adverse events.",
      "11.4 Independent Verification",
      "Users should consult licensed healthcare professionals or pharmacists before initiating, discontinuing, or modifying any medication regimen.",
      "11.5 No Warranty",
      "The Company does not guarantee the completeness, accuracy, or reliability of medication-related information.",
    ],
  },
  {
    title: "12. LAB RESULTS DISCLAIMER",
    paragraphs: [
      "12.1 Upload Features",
      "Users may upload laboratory reports, pathology records, imaging reports, diagnostic documents, and related materials.",
      "12.2 Informational Purposes",
      "Any summaries, interpretations, or analyses generated by the Platform are provided solely for informational purposes.",
      "12.3 No Diagnosis",
      "The Platform is not intended to diagnose diseases or replace consultation with qualified healthcare professionals.",
      "12.4 Verification Required",
      "Users are solely responsible for reviewing laboratory results with licensed physicians.",
    ],
  },
  {
    title: "13. FITNESS AND NUTRITION DISCLAIMER",
    paragraphs: [
      "13.1 Wellness Guidance",
      "The Platform may provide fitness programs, yoga routines, meal suggestions, nutrition recommendations, lifestyle guidance, and wellness content.",
      "13.2 Individual Differences",
      "Results may vary among individuals due to age, medical history, genetics, physical condition, and other factors.",
      "13.3 Exercise Risks",
      "Participation in exercise activities carries inherent risks including injury, illness, disability, or death.",
      "13.4 Professional Consultation",
      "Users should consult healthcare professionals before beginning any fitness or dietary program.",
      "13.5 No Guarantees",
      "AUMNIYA does not guarantee weight loss, improved health outcomes, or any specific results.",
    ],
  },
  {
    title: "14. MENTAL HEALTH DISCLAIMER",
    paragraphs: [
      "14.1 Informational Nature",
      "Mental wellness features are educational and informational in nature.",
      "14.2 No Therapy Services",
      "The Platform does not provide psychotherapy, psychiatric treatment, counseling, crisis intervention, or mental health services.",
      "14.3 Emergency Situations",
      "Users experiencing suicidal ideation, severe emotional distress, or psychiatric emergencies should immediately seek professional assistance.",
      "14.4 No Healthcare Relationship",
      "Use of mental wellness features does not establish a therapist-patient or psychiatrist-patient relationship.",
    ],
  },
  {
    title: "15. PREGNANCY DISCLAIMER",
    paragraphs: [
      "15.1 Pregnancy Content",
      "The Platform may provide pregnancy-related wellness information and educational materials.",
      "15.2 No Medical Advice",
      "Pregnancy-related information is not intended to replace prenatal care or professional medical guidance.",
      "15.3 High-Risk Pregnancies",
      "Individuals with high-risk pregnancies should seek immediate guidance from qualified healthcare providers.",
      "15.4 User Responsibility",
      "Users acknowledge that pregnancy-related decisions should be made in consultation with licensed medical professionals.",
    ],
  },
  {
    title: "16. GENETIC AND PHARMACOGENOMIC DISCLAIMER",
    paragraphs: [
      "16.1 Future Features",
      "The Platform may offer pharmacogenomic and genetic wellness capabilities.",
      "16.2 Informational Use",
      "Genetic information and pharmacogenomic insights are intended solely for informational purposes.",
      "16.3 No Clinical Decisions",
      "Genetic outputs should not be used as the sole basis for treatment decisions.",
      "16.4 Professional Interpretation",
      "Users should consult appropriately qualified physicians and specialists regarding genetic information.",
    ],
  },
  {
    title: "17. FAMILY ACCOUNTS",
    paragraphs: [
      "17.1 Authorized Users",
      "The Platform may permit Users to create family accounts, dependent profiles, and caregiver accounts.",
      "17.2 Authority",
      "Users represent and warrant that they possess all necessary authority to manage information relating to dependents or other authorized individuals.",
      "17.3 Caregiver Access",
      "Caregivers may access information only to the extent authorized by applicable law and the relevant User.",
      "17.4 User Responsibility",
      "The primary account holder remains responsible for all activity conducted through associated accounts.",
      "17.5 Minors",
      "Parents or legal guardians are solely responsible for supervising minors utilizing the Platform.",
    ],
  },
  {
    title: "18. ELIGIBILITY",
    paragraphs: [
      "18.1 Legal Capacity",
      "Users must possess the legal capacity to enter into binding agreements.",
      "18.2 Minimum Age",
      "Users under eighteen (18) years of age may use the Platform only with parental or legal guardian authorization.",
      "18.3 Prohibited Jurisdictions",
      "The Platform may not be available in certain jurisdictions.",
      "18.4 Compliance with Laws",
      "Users agree to comply with all applicable laws, rules, and regulations.",
      "18.5 Suspension of Access",
      "The Company reserves the right to restrict access to any User who fails to satisfy eligibility requirements.",
    ],
  },
  {
    title: "19. USER ACCOUNTS",
    paragraphs: [
      "19.1 Registration",
      "Certain features of the Platform require the creation of an account.",
      "Users agree to provide accurate, complete, and current information and to promptly update such information as necessary.",
      "19.2 Credentials",
      TERMS_CREDENTIALS_SENTENCE,
      "19.3 Unauthorized Access",
      "Users shall immediately notify AUMNIYA LLC of any unauthorized access, misuse, or suspected compromise of their accounts.",
      "19.4 Responsibility for Activities",
      "Users are responsible for all activities occurring under their accounts, whether authorized or unauthorized.",
      "19.5 Verification",
      "The Company reserves the right to require identity verification and additional authentication procedures.",
    ],
  },
  {
    title: "20. USER CONTENT",
    paragraphs: [
      "20.1 Ownership",
      "Subject to the licenses granted herein, Users retain ownership rights in User Content.",
      "20.2 Responsibility",
      "Users are solely responsible for all User Content uploaded, transmitted, stored, or otherwise made available through the Platform.",
      "20.3 Accuracy",
      "Users represent and warrant that User Content is accurate and that they possess all rights necessary to provide such content.",
      "20.4 Prohibited Content",
      "Users shall not upload content that:",
      "(a) violates applicable laws;",
      "(b) infringes intellectual property rights;",
      "(c) contains malicious code;",
      "(d) is fraudulent or deceptive;",
      "(e) is defamatory, obscene, or unlawful;",
      "(f) violates privacy rights.",
      "20.5 Monitoring",
      "The Company shall have no obligation to monitor User Content but reserves the right to review, remove, or restrict access to such content.",
    ],
  },
  {
    title: "21. LICENSE GRANTED BY USERS",
    paragraphs: [
      "21.1 Platform License",
      "Users grant AUMNIYA LLC a worldwide, non-exclusive, royalty-free, transferable, sublicensable license to host, reproduce, process, transmit, analyze, store, and display User Content solely for the purpose of operating and improving the Platform.",
      "21.2 Technical Copies",
      "Users authorize the creation of temporary, backup, archival, and technical copies required for operation of the Platform.",
      "21.3 Anonymization",
      "To the extent permitted by law, de-identified and aggregated information may be utilized for analytics, machine learning, product enhancement, and research.",
      "21.4 Survival",
      "Licenses reasonably necessary for legal compliance, backup, and archival purposes shall survive termination.",
    ],
  },
  {
    title: "22. PROHIBITED CONDUCT",
    paragraphs: [
      "Users shall not:",
      "(a) reverse engineer the Platform;",
      "(b) interfere with Platform operations;",
      "(c) circumvent security mechanisms;",
      "(d) use automated bots without authorization;",
      "(e) scrape or extract data;",
      "(f) exploit vulnerabilities;",
      "(g) upload malicious software;",
      "(h) impersonate another person;",
      "(i) engage in unlawful conduct;",
      "(j) violate applicable healthcare laws;",
      "(k) infringe intellectual property rights;",
      "(l) use the Platform to create competing services;",
      "(m) attempt unauthorized access to systems;",
      "(n) misuse APIs or SDKs.",
      "Violation of this Article may result in immediate suspension or termination.",
    ],
  },
  {
    title: "23. THIRD-PARTY SERVICES",
    paragraphs: [
      "23.1 Third-Party Providers",
      "The Platform may integrate with third-party products and services.",
      "23.2 No Responsibility",
      "AUMNIYA LLC does not own or control third-party services and assumes no responsibility for their availability, content, security, functionality, or accuracy.",
      "23.3 Independent Terms",
      "Use of third-party services may be governed by separate agreements.",
      "23.4 Disclaimer",
      "The Company disclaims all liability arising from third-party products and services.",
    ],
  },
  {
    title: "24. WEARABLE DEVICES",
    paragraphs: [
      "24.1 Integrations",
      "The Platform may support wearable devices and related technologies.",
      "24.2 Device Limitations",
      "Device measurements may be inaccurate, delayed, interrupted, or unavailable.",
      "24.3 No Guarantee",
      "The Company does not guarantee the accuracy or reliability of device-generated information.",
      "24.4 Independent Verification",
      "Users should independently verify measurements before relying upon them.",
    ],
  },
  {
    title: "25. SMART MIRRORS AND CAMERA-BASED SYSTEMS",
    paragraphs: [
      "25.1 Future Technologies",
      "The Platform may support smart mirrors, camera systems, facial analysis technologies, and related health-monitoring capabilities.",
      "25.2 Informational Purposes",
      "Outputs generated through such technologies are intended solely for wellness and informational purposes.",
      "25.3 No Medical Device Claims",
      "Nothing within the Platform should be interpreted as representing FDA approval, CDSCO approval, or regulatory certification unless expressly stated.",
      "25.4 Accuracy Disclaimer",
      "Environmental conditions and technical limitations may affect performance.",
    ],
  },
  {
    title: "26. SUBSCRIPTIONS",
    paragraphs: [
      "26.1 Paid Services",
      "Certain features may require paid subscriptions.",
      "26.2 Billing",
      "Subscription fees shall be charged according to the selected plan.",
      "26.3 Automatic Renewal",
      "Unless otherwise specified, subscriptions automatically renew.",
      "26.4 Cancellation",
      "Users may cancel subscriptions in accordance with applicable policies.",
      "26.5 Modifications",
      "The Company may modify pricing, plans, features, or subscription structures upon reasonable notice.",
    ],
  },
  {
    title: "27. PAYMENTS",
    paragraphs: [
      "27.1 Payment Methods",
      "Users authorize the Company and its payment processors to charge payment methods provided by Users.",
      "27.2 Taxes",
      "Fees are exclusive of taxes unless otherwise required by law.",
      "27.3 Failed Payments",
      "The Company reserves the right to suspend services for failed or declined payments.",
      "27.4 Collection",
      "The Company may utilize collection agencies and pursue lawful remedies for unpaid amounts.",
    ],
  },
  {
    title: "28. REFUNDS",
    paragraphs: [
      "28.1 General Rule",
      "Except as required by applicable law, payments are non-refundable.",
      "28.2 Promotional Services",
      "Promotional discounts and credits possess no cash value.",
      "28.3 Billing Errors",
      "Verified billing errors may be corrected at the Company's discretion.",
      "28.4 Chargebacks",
      "Improper chargebacks may result in account suspension and recovery actions.",
    ],
  },
  {
    title: "29. INTELLECTUAL PROPERTY",
    paragraphs: [
      "29.1 Ownership",
      "The Platform and all related technologies, software, source code, object code, algorithms, models, databases, interfaces, graphics, layouts, text, content, trademarks, logos, audiovisual materials, workflows, designs, documentation, APIs, SDKs, and all associated intellectual property rights are and shall remain the exclusive property of AUMNIYA LLC and its licensors.",
      "29.2 Reservation of Rights",
      "Except for the limited rights expressly granted herein, no rights, title, or interests are transferred to Users.",
      "29.3 Proprietary Rights",
      "All patents, copyrights, trade secrets, trademarks, know-how, and other intellectual property rights are reserved.",
      "29.4 Restrictions",
      "Users shall not:",
      "(a) copy;",
      "(b) reproduce;",
      "(c) distribute;",
      "(d) reverse engineer;",
      "(e) modify;",
      "(f) create derivative works;",
      "(g) commercially exploit;",
      "(h) sublicense;",
      "(i) sell or lease any portion of the Platform.",
    ],
  },
  {
    title: "30. OWNERSHIP OF GENERATED CONTENT",
    paragraphs: [
      "30.1 AI Outputs",
      "Subject to applicable law and these Terms, Users may utilize Generated Content produced specifically for them.",
      "30.2 Company Ownership",
      "The Company retains ownership of:",
      "(a) AI models;",
      "(b) training methodologies;",
      "(c) prompts;",
      "(d) algorithms;",
      "(e) software;",
      "(f) datasets;",
      "(g) workflows;",
      "(h) system architectures.",
      "30.3 Similar Outputs",
      "Due to the nature of artificial intelligence, outputs generated for different users may be identical or substantially similar.",
      "30.4 No Exclusivity",
      "The Company does not guarantee exclusivity of Generated Content.",
      "30.5 Feedback",
      "Any suggestions, feedback, or ideas submitted by Users may be used by AUMNIYA LLC without restriction or compensation.",
    ],
  },
  {
    title: "31. COPYRIGHT POLICY",
    paragraphs: [
      "31.1 Respect for Rights",
      "The Company respects intellectual property rights.",
      "31.2 Infringement Notices",
      "Claims of copyright infringement shall contain sufficient information to identify:",
      "(a) the copyrighted work;",
      "(b) the allegedly infringing material;",
      "(c) ownership rights;",
      "(d) contact information;",
      "(e) a statement made in good faith.",
      "31.3 Removal",
      "The Company reserves the right to remove allegedly infringing content.",
      "31.4 Repeat Infringers",
      "Accounts associated with repeated violations may be terminated.",
    ],
  },
  {
    title: "32. TRADEMARK PROTECTION",
    paragraphs: [
      "32.1 Marks",
      "\"MyTara™,\" \"TARA AI Assistant,\" and associated marks are proprietary trademarks of AUMNIYA LLC.",
      "32.2 Restrictions",
      "Users shall not use Company trademarks without prior written authorization.",
      "32.3 No Implied Rights",
      "Nothing contained herein grants any trademark license.",
    ],
  },
  {
    title: "33. APIS AND SDKS",
    paragraphs: [
      "33.1 Availability",
      "The Company may make APIs, SDKs, developer tools, and technical documentation available.",
      "33.2 Restrictions",
      "Users shall not:",
      "(a) misuse APIs;",
      "(b) exceed rate limits;",
      "(c) compromise security;",
      "(d) access unauthorized data;",
      "(e) create unlawful applications.",
      "33.3 Modifications",
      "APIs and SDKs may be modified or discontinued at any time.",
      "33.4 Suspension",
      "The Company may suspend developer access without liability.",
    ],
  },
  {
    title: "34. SERVICE AVAILABILITY",
    paragraphs: [
      "34.1 No Guarantee",
      "The Platform is provided on an availability basis and uninterrupted access is not guaranteed.",
      "34.2 Maintenance",
      "The Company may perform maintenance, upgrades, and modifications.",
      "34.3 Interruptions",
      "Interruptions may occur because of:",
      "(a) system failures;",
      "(b) cyberattacks;",
      "(c) third-party outages;",
      "(d) force majeure events;",
      "(e) network disruptions.",
      "34.4 No Liability",
      "The Company shall not be liable for service interruptions.",
    ],
  },
  {
    title: "35. SUSPENSION",
    paragraphs: [
      "35.1 Grounds",
      "The Company may suspend access for:",
      "(a) security concerns;",
      "(b) violations of these Terms;",
      "(c) fraudulent activity;",
      "(d) nonpayment;",
      "(e) legal requirements.",
      "35.2 Immediate Action",
      "Suspension may occur without prior notice.",
      "35.3 Investigation",
      "The Company may investigate suspected misconduct.",
    ],
  },
  {
    title: "36. TERMINATION",
    paragraphs: [
      "36.1 User Termination",
      "Users may terminate accounts at any time.",
      "36.2 Company Termination",
      "The Company may terminate accounts for any lawful reason.",
      "36.3 Effect of Termination",
      "Upon termination:",
      "(a) access rights cease;",
      "(b) licenses granted by the Company terminate;",
      "(c) certain information may be retained as required by law.",
      "36.4 Survival",
      "Provisions relating to intellectual property, limitations of liability, indemnification, dispute resolution, and other provisions intended to survive shall survive termination.",
    ],
  },
  {
    title: "37. DISCLAIMER OF WARRANTIES",
    paragraphs: [
      "37.1 \"AS IS\"",
      "THE PLATFORM IS PROVIDED \"AS IS,\" \"AS AVAILABLE,\" AND WITH ALL FAULTS.",
      "37.2 No Warranties",
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW, AUMNIYA LLC DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING:",
      "(a) MERCHANTABILITY;",
      "(b) FITNESS FOR A PARTICULAR PURPOSE;",
      "(c) NON-INFRINGEMENT;",
      "(d) ACCURACY;",
      "(e) RELIABILITY;",
      "(f) SECURITY;",
      "(g) AVAILABILITY.",
      "37.3 AI Limitations",
      "THE COMPANY DOES NOT WARRANT THAT AI OUTPUTS WILL BE ACCURATE, COMPLETE, OR ERROR-FREE.",
      "37.4 THIRD-PARTY SERVICES",
      "THE COMPANY DISCLAIMS ALL RESPONSIBILITY FOR THIRD-PARTY PRODUCTS AND SERVICES.",
    ],
  },
  {
    title: "38. LIMITATION OF LIABILITY",
    paragraphs: [
      "38.1 Excluded Damages",
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW, AUMNIYA LLC SHALL NOT BE LIABLE FOR:",
      "(a) INDIRECT DAMAGES;",
      "(b) INCIDENTAL DAMAGES;",
      "(c) SPECIAL DAMAGES;",
      "(d) CONSEQUENTIAL DAMAGES;",
      "(e) EXEMPLARY DAMAGES;",
      "(f) LOSS OF PROFITS;",
      "(g) LOSS OF DATA;",
      "(h) LOSS OF GOODWILL;",
      "(i) BUSINESS INTERRUPTION.",
      "38.2 Aggregate Liability Cap",
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE AGGREGATE LIABILITY OF AUMNIYA LLC SHALL NOT EXCEED THE GREATER OF:",
      "(a) ONE HUNDRED UNITED STATES DOLLARS (US$100); OR",
      "(b) THE AMOUNTS PAID BY THE USER TO THE COMPANY DURING THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.",
      "38.3 Basis of Bargain",
      "THE PARTIES ACKNOWLEDGE THAT THE LIMITATIONS CONTAINED HEREIN FORM AN ESSENTIAL BASIS OF THE BARGAIN BETWEEN THE PARTIES.",
    ],
  },
  {
    title: "39. INDEMNIFICATION",
    paragraphs: [
      "39.1 User Indemnification",
      "To the maximum extent permitted by applicable law, Users agree to defend, indemnify, and hold harmless AUMNIYA LLC and its affiliates, subsidiaries, parent companies, members, managers, officers, directors, employees, contractors, licensors, agents, successors, and assigns from and against any and all claims, actions, liabilities, losses, damages, judgments, penalties, fines, settlements, costs, and expenses, including reasonable attorneys' fees, arising out of or relating to:",
      "(a) use of the Platform;",
      "(b) User Content;",
      "(c) violation of these Terms;",
      "(d) violation of applicable laws;",
      "(e) infringement of intellectual property rights;",
      "(f) misuse of AI-generated outputs;",
      "(g) reliance upon information obtained through the Platform;",
      "(h) interactions with third parties.",
      "39.2 Cooperation",
      "Users shall cooperate fully in the defense of any matter subject to indemnification.",
      "39.3 Control of Defense",
      "AUMNIYA LLC reserves the exclusive right to assume the defense and settlement of any claim.",
    ],
  },
  {
    title: "40. FORCE MAJEURE",
    paragraphs: [
      "40.1 Excused Performance",
      "Neither party shall be liable for delays or failures in performance resulting from circumstances beyond reasonable control.",
      "40.2 Events",
      "Force majeure events include:",
      "(a) natural disasters;",
      "(b) pandemics;",
      "(c) epidemics;",
      "(d) war;",
      "(e) terrorism;",
      "(f) civil unrest;",
      "(g) governmental actions;",
      "(h) labor disputes;",
      "(i) internet outages;",
      "(j) cyberattacks;",
      "(k) power failures;",
      "(l) telecommunications failures;",
      "(m) failures of suppliers and service providers.",
      "40.3 Resumption",
      "Affected obligations shall resume when the force majeure event ceases.",
    ],
  },
  {
    title: "41. DATA PROTECTION - INDIA (DPDPA 2023)",
    paragraphs: [
      "41.1 Applicable Law",
      "The collection, processing, storage, and transfer of personal data and Health Data relating to Users located in India is governed by the Digital Personal Data Protection Act, 2023 (“DPDPA”) and rules made thereunder.",
      "41.2 Data Fiduciary",
      "AUMNIYA LLC acts as the Data Fiduciary with respect to personal data collected through the Platform.",
      "41.3 Consent",
      "The Company collects and processes personal data and Health Data only upon obtaining free, specific, informed, unconditional, and unambiguous consent from the User, as required under the DPDPA.",
      "Consent is obtained prior to or at the point of data collection.",
      "Users may withdraw consent at any time by contacting the Company at legal@mytara.ai.",
      "Withdrawal of consent shall not affect the lawfulness of processing prior to withdrawal but may result in the inability to access certain features of the Platform.",
      "41.4 Purpose Limitation",
      "Personal data and Health Data collected through the Platform is used solely for the purposes disclosed at the time of collection, including but not limited to:",
      "(a) providing Platform features and services;",
      "(b) improving AI model performance using anonymized and aggregated data;",
      "(c) communicating service updates and notifications; and",
      "(d) complying with legal obligations.",
      "41.5 Data Principal Rights",
      "Users, as Data Principals under the DPDPA, have the right to:",
      "(a) obtain a summary of personal data held by the Company;",
      "(b) correct or update inaccurate personal data;",
      "(c) request erasure of personal data where the purpose of collection has been fulfilled or consent has been withdrawn;",
      "(d) nominate another individual to exercise these rights in the event of death or incapacity; and",
      "(e) grieve any action or inaction of the Company with respect to personal data.",
      "41.6 Grievance Redressal",
      "Users may submit data-related grievances to the Company’s designated Grievance Officer:",
      "Email: legal@mytara.ai",
      "Website: www.mytara.ai",
      "The Company shall acknowledge grievances within 48 hours and resolve them within a period not exceeding 30 days from receipt.",
      "41.7 Data Retention",
      "Personal data and Health Data shall not be retained beyond the period necessary to fulfil the purpose for which it was collected, or as required by applicable law, whichever is later.",
      "Upon expiry of the retention period, data shall be deleted or anonymized.",
      "41.8 Cross-Border Transfers",
      "Where personal data is transferred outside India, such transfers shall be conducted only in accordance with applicable provisions of the DPDPA and any rules or notifications issued by the Government of India regarding permissible transfer destinations.",
      "41.9 Children’s Data",
      "The Company does not knowingly collect personal data from children under the age of eighteen (18) without verifiable parental or guardian consent, as required under the DPDPA.",
    ],
  },
  {
    title: "42. GOVERNING LAW",
    paragraphs: [
      "42.1 Applicable Law",
      "These Terms shall be governed by and construed in accordance with the laws of India, without regard to principles of conflicts of laws.",
      "42.2 Mandatory Laws",
      "Nothing contained herein shall exclude mandatory consumer protections or statutory rights that cannot lawfully be waived.",
    ],
  },
  {
    title: "43. ARBITRATION",
    paragraphs: [
      "43.1 Arbitration Agreement",
      "Except where prohibited by law, any dispute, controversy, or claim arising out of or relating to these Terms or the Platform shall be resolved exclusively through binding arbitration.",
      "43.2 Applicable Law",
      "Arbitration shall be conducted pursuant to the Arbitration and Conciliation Act, 1996.",
      "43.3 Seat and Venue",
      "The seat and venue of arbitration shall be New Delhi, India.",
      "43.4 Language",
      "Arbitration proceedings shall be conducted in the English language.",
      "43.5 Number of Arbitrators",
      "Disputes shall be resolved by a sole arbitrator appointed in accordance with applicable law.",
      "43.6 Confidentiality",
      "Arbitration proceedings and awards shall remain confidential.",
      "43.7 Equitable Relief",
      "Nothing herein shall prevent either party from seeking temporary or permanent equitable relief from courts having jurisdiction.",
    ],
  },
  {
    title: "44. CLASS ACTION WAIVER",
    paragraphs: [
      "44.1 Individual Claims",
      "To the fullest extent permitted by applicable law, Users agree that disputes shall be brought solely in an individual capacity.",
      "44.2 No Representative Actions",
      "Users waive any right to participate in:",
      "(a) class actions;",
      "(b) collective actions;",
      "(c) representative actions;",
      "(d) consolidated proceedings.",
      "44.3 Severability",
      "If this waiver is determined to be unenforceable, the remaining provisions shall remain in effect to the maximum extent permitted by law.",
    ],
  },
  {
    title: "45. INJUNCTIVE RELIEF",
    paragraphs: [
      "45.1 Equitable Remedies",
      "Users acknowledge that unauthorized use of the Platform may result in irreparable harm.",
      "45.2 Right to Injunctive Relief",
      "AUMNIYA LLC shall be entitled to seek injunctive relief, equitable relief, and specific performance without the necessity of posting bond or proving actual damages.",
    ],
  },
  {
    title: "46. GENERAL PROVISIONS",
    paragraphs: [
      "46.1 Entire Agreement",
      "These Terms constitute the complete and exclusive agreement between the parties and supersede all prior agreements, understandings, and communications.",
      "46.2 Modifications",
      "AUMNIYA LLC reserves the right to amend these Terms at any time.",
      "Continued use of the Platform constitutes acceptance of revised Terms.",
      "46.3 Assignment",
      "Users may not assign rights or obligations without prior written consent.",
      "AUMNIYA LLC may assign these Terms without restriction.",
      "46.4 Waiver",
      "Failure to enforce any provision shall not constitute a waiver.",
      "46.5 Severability",
      "If any provision is found invalid or unenforceable, the remaining provisions shall remain effective.",
      "46.6 Relationship of Parties",
      "Nothing herein creates a partnership, joint venture, employment relationship, agency relationship, or fiduciary relationship.",
      "46.7 No Third-Party Beneficiaries",
      "Except as expressly stated, these Terms confer no rights upon third parties.",
      "46.8 Electronic Communications",
      "Users consent to receiving notices and communications electronically.",
      "Electronic records and signatures shall have the same force and effect as physical documents and handwritten signatures.",
      "46.9 Export Compliance",
      "Users agree to comply with all applicable export control laws and sanctions regulations.",
      "46.10 Headings",
      "Article and section headings are included solely for convenience and shall not affect interpretation.",
      "46.11 Survival",
      "Any provisions which by their nature should survive termination shall survive termination.",
      "46.12 Contact Information",
      "Questions regarding these Terms may be directed to:",
      "AUMNIYA LLC",
      "MyTara™ Platform",
      "Legal Department",
      "Email: legal@mytara.ai",
      "Website: www.mytara.ai",
      "ACKNOWLEDGMENT",
      "BY ACCESSING OR USING MYTARA™, THE USER ACKNOWLEDGES THAT THE USER HAS READ, UNDERSTOOD, AND AGREES TO BE LEGALLY BOUND BY THESE TERMS AND CONDITIONS.",
      "THE USER FURTHER ACKNOWLEDGES THAT THE PLATFORM IS NOT A SUBSTITUTE FOR PROFESSIONAL MEDICAL CARE, DIAGNOSIS, OR TREATMENT.",
    ],
  }
];

export function getTermsFullDocumentUrl(): string {
  if (globalThis.window === undefined) return TERMS_FULL_DOCUMENT_PATH;
  return `${globalThis.location.origin}${TERMS_FULL_DOCUMENT_PATH}`;
}
